import React, { useState, useEffect } from 'react';
import ConfirmPurchaseButton from './confirm_purchase-button';
import RequestButton from './request-button';

interface SavedPurchasedProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSuccess?: (message: string) => void;
  onConfirmError?: (message: string) => void;
  onRegisterRefresh?: (refreshFn: () => void) => void;
}

const SavedPurchased: React.FC<SavedPurchasedProps> = ({ isOpen, onClose, onConfirmSuccess, onConfirmError, onRegisterRefresh }) => {
  const [pendingPurchases, setPendingPurchases] = useState<any[]>([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);
  const [expandedPurchases, setExpandedPurchases] = useState<Set<number | string>>(new Set());
  const [receivePromptPurchase, setReceivePromptPurchase] = useState<any | null>(null);
  const [receivingPurchaseId, setReceivingPurchaseId] = useState<number | string | null>(null);
  const [quantityUpdatingItemId, setQuantityUpdatingItemId] = useState<number | string | null>(null);
  const [editingQuantityItemId, setEditingQuantityItemId] = useState<number | string | null>(null);
  const [quantityEditValue, setQuantityEditValue] = useState<{ [key: string]: string }>({});
  const [entityType, setEntityType] = useState<string>('');
  const [waitingConfirmIds, setWaitingConfirmIds] = useState<Set<number | string>>(new Set());

  const getEntityType = (): string => {
    try {
      const raw = localStorage.getItem('pharmaEntityType');
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      return typeof parsed?.entity_type === 'string' ? parsed.entity_type : '';
    } catch {
      return '';
    }
  };

  const isDoctorAssociated = entityType === 'doctor_associated';

  const getPurchaseItemId = (item: any): number | string | null => {
    if (item?.id != null) return item.id;
    if (item?.item_id != null) return item.item_id;
    if (item?.purchase_item_id != null) return item.purchase_item_id;
    return null;
  };

  const isPurchaseReceived = (purchase: any): boolean => {
    return (purchase?.status || '').toLowerCase() === 'received';
  };

  const startEditQuantity = (item: any) => {
    const itemId = getPurchaseItemId(item);
    if (itemId == null) return;
    setEditingQuantityItemId(itemId);
    setQuantityEditValue(prev => ({ ...prev, [String(itemId)]: String(item.quantity ?? '') }));
  };

  const cancelEditQuantity = (itemId: number | string) => {
    setEditingQuantityItemId(null);
    setQuantityEditValue(prev => {
      const next = { ...prev };
      delete next[String(itemId)];
      return next;
    });
  };

  const updateItemQuantity = async (itemId: number | string, quantity: number, purchase: any) => {
    setQuantityUpdatingItemId(itemId);
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/items/${itemId}`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ quantity })
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP ${response.status}`);
      }
      cancelEditQuantity(itemId);
      await fetchPendingPurchases();
    } catch (err: any) {
      if (onConfirmError) {
        onConfirmError(err?.message || 'Failed to update quantity.');
      } else {
        alert(err?.message || 'Failed to update quantity.');
      }
    } finally {
      setQuantityUpdatingItemId(null);
    }
  };

  const handleQuantitySave = (item: any, purchase: any) => {
    const itemId = getPurchaseItemId(item);
    if (!itemId) return;
    const valueStr = quantityEditValue[String(itemId)] ?? String(item.quantity ?? '');
    const val = parseInt(valueStr, 10);
    if (isNaN(val) || val < 1) return;
    const originalQty = Number(item.quantity) || 0;
    if (val === originalQty) {
      cancelEditQuantity(itemId);
      return;
    }
    updateItemQuantity(itemId, val, purchase);
  };

  const [deletingItemId, setDeletingItemId] = useState<number | string | null>(null);

  const handleDeleteItem = (item: any, purchase: any) => {
    const itemId = getPurchaseItemId(item);
    if (!itemId) return;
    if (!window.confirm('Remove this medicine from the purchase? Quantity will be set to 0.')) return;
    setDeletingItemId(itemId);
    updateItemQuantity(itemId, 0, purchase).finally(() => setDeletingItemId(null));
  };

  const fetchPendingPurchases = async () => {
    try {
      setIsLoadingPurchases(true);
      setPurchasesError(null);
      
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
        
      });
      console.log('Pending purchases response:', response);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch purchases (Status: ${response.status}): ${errorText}`);
      }

      const data = await response.json();
      const purchases = data.pending_purchases || data.purchases || data.data || data || [];
      console.log('[SavedPurchased] Pending purchases response:', purchases);
      setPendingPurchases(Array.isArray(purchases) ? purchases : []);
      
    } catch (error: any) {
      console.error('Error fetching pending purchases:', error);
      setPurchasesError(error.message);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  // Register refresh function with parent
  useEffect(() => {
    if (onRegisterRefresh) {
      onRegisterRefresh(fetchPendingPurchases);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterRefresh]);

  // Fetch pending purchases when component opens
  useEffect(() => {
    if (isOpen) {
      setEntityType(getEntityType());
      fetchPendingPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleConfirmSuccess = (purchase?: any) => {
    fetchPendingPurchases();
    if (onConfirmSuccess) {
      onConfirmSuccess('Purchase confirmed successfully!');
    }
    if (purchase) {
      setReceivePromptPurchase(purchase);
    }
  };

  const handleConfirmError = (error: string) => {
    // Show error notification if callback provided
    if (onConfirmError) {
      onConfirmError(`Failed to confirm purchase: ${error}`);
    } else {
      alert(`Failed to confirm purchase: ${error}`);
    }
  };

  const buildPurchaseEditRequestBody = (purchase: any) => {
    const purchaseId = resolvePurchaseId(purchase) ?? purchase?.pending_id ?? purchase?.id ?? '';
    const purchaseItems = Array.isArray(purchase?.items)
      ? purchase.items.map((item: any, index: number) => {
          const itemId = item?.id ?? item?.item_id ?? item?.purchase_item_id ?? index;
          const normalizedItem = {
            ...item,
            id: itemId,
            medicine_name: item?.medicine_name || item?.name || '',
            quantity: Number(item?.quantity || 0),
            unit_cost: Number(item?.unit_cost || 0),
            line_total: Number(item?.line_total || item?.total || 0),
            mrp: Number(item?.mrp || item?.unit_cost || 0),
            expiry: item?.expiry || '',
            batch_number: item?.batch_number || '',
            storage_location: item?.storage_location || '',
          };

          return {
            ...normalizedItem,
          };
        })
      : [];

    const normalizedPurchase = {
      ...purchase,
      id: purchaseId || null,
      distributor_name: purchase?.distributor_name || purchase?.distributor?.name || '',
      total_amount: Number(purchase?.total_amount || 0),
      item_count: purchaseItems.length,
      items: purchaseItems,
    };

    return {
      action: 'purchase',
      reference_id: String(purchaseId),
      payload: {
        inventory: {
          new_data: {
            // Send the complete purchase data
            ...normalizedPurchase,
            items: purchaseItems
          }
        }
      },
    };
  };

  const markPurchaseAsWaitingToConfirm = (purchase: any) => {
    const purchaseId = resolvePurchaseId(purchase);
    if (purchaseId == null) return;

    setWaitingConfirmIds(prev => {
      const next = new Set(prev);
      next.add(purchaseId);
      return next;
    });

    setPendingPurchases(prev =>
      prev.map(item => {
        const itemId = resolvePurchaseId(item);
        if (itemId !== purchaseId) return item;
        return { ...item, status: 'pending_confirmation' };
      })
    );
  };

  const getDisplayStatus = (purchase: any): string => {
    const purchaseId = resolvePurchaseId(purchase);
    if (purchaseId != null && waitingConfirmIds.has(purchaseId)) {
      return 'waiting to confirm';
    }
    return purchase.status ? purchase.status.replace('_', ' ') : 'Pending';
  };

  const isPendingConfirmation = (purchase: any): boolean => {
    const purchaseId = resolvePurchaseId(purchase);
    if (purchaseId != null && waitingConfirmIds.has(purchaseId)) {
      return true;
    }
    return purchase.status === 'pending_confirmation';
  };

  const getAdminConfirmationStatus = (purchase: any): string => {
    return purchase?.admin_confirm_request_status || '';
  };

  const hasAdminConfirmationStatus = (purchase: any): boolean => {
    const status = getAdminConfirmationStatus(purchase);
    return status === 'pending' || status === 'approved';
  };

  const isAdminStatusPending = (purchase: any): boolean => {
    return getAdminConfirmationStatus(purchase) === 'pending';
  };

  const isAdminStatusApproved = (purchase: any): boolean => {
    return getAdminConfirmationStatus(purchase) === 'approved';
  };

  const handleRequestActionSuccess = (purchase: any) => {
    markPurchaseAsWaitingToConfirm(purchase);
    fetchPendingPurchases();
    if (onConfirmSuccess) {
      onConfirmSuccess('Request sent successfully. Waiting to confirm.');
    }
  };

  const handleRequestError = (error: string) => {
    if (onConfirmError) {
      onConfirmError(`Failed to send request: ${error}`);
    } else {
      alert(`Failed to send request: ${error}`);
    }
  };

  const handleReceiveSuccess = () => {
    fetchPendingPurchases();
    if (onConfirmSuccess) {
      onConfirmSuccess('Purchase marked as received!');
    }
    setReceivePromptPurchase(null);
  };

  const handleReceiveError = (error: string) => {
    if (onConfirmError) {
      onConfirmError(`Failed to mark as received: ${error}`);
    } else {
      alert(`Failed to mark as received: ${error}`);
    }
  };

  const resolvePurchaseId = (purchase: any): number | string | null => {
    return (
      purchase?.purchase_id ??
      purchase?.purchaseId ??
      purchase?.id ??
      purchase?.purchase?.id ??
      purchase?.purchase?.purchase_id ??
      null
    );
  };

  const handleMarkAsReceived = async (purchase: any) => {
    const purchaseId = resolvePurchaseId(purchase);
    if (!purchaseId) {
      handleReceiveError('Purchase ID missing for receive action.');
      return;
    }
    try {
      setReceivingPurchaseId(purchaseId);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/receive`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          purchase_id: purchaseId
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to mark purchase as received');
      }
      await response.json();
      handleReceiveSuccess();
    } catch (error: any) {
      console.error('Error marking purchase as received:', error);
      // Enhanced CORS error handling
      let errorMessage = error.message || 'Unknown error';
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('Access-Control-Allow-Origin') || error.message.includes('network'))) {
        errorMessage = 'CORS Error: Unable to connect to the server. Please check your connection and ensure the server allows requests from this origin.';
      }
      handleReceiveError(errorMessage);
    } finally {
      setReceivingPurchaseId(null);
    }
  };

  const togglePurchaseItems = (purchaseId: number | string) => {
    setExpandedPurchases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(purchaseId)) {
        newSet.delete(purchaseId);
      } else {
        newSet.add(purchaseId);
      }
      return newSet;
    });
  };

  // Helper function to check if payment is fully settled
  const isPaymentFullySettled = (purchase: any): boolean => {
    const totalAmount = purchase.total_amount || 0;
    const settledAmount = purchase.settled_amount || 0;
    const unsettledAmount = purchase.unsettled_amount !== undefined 
      ? purchase.unsettled_amount 
      : (totalAmount - settledAmount);
    return unsettledAmount <= 0 && settledAmount >= totalAmount && totalAmount > 0;
  };

  if (!isOpen) return null;

  const isMobile = window.innerWidth < 768;
  const activeReceiveId = resolvePurchaseId(receivePromptPurchase);

  return (
    <>
      <div style={{
        marginBottom: '1.5rem'
      }}>
        <h3 style={{
          fontSize: isMobile ? '1.1rem' : '1.3rem',
          fontWeight: '700',
          color: '#1f2937',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: isMobile ? '32px' : '40px',
            height: isMobile ? '32px' : '40px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
          }}>
            <svg width={isMobile ? "16" : "20"} height={isMobile ? "16" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
            </svg>
          </div>
          Saved Purchases ({pendingPurchases.length})
        </h3>
        <p style={{
          margin: '0.4rem 0 0',
          fontSize: isMobile ? '0.8rem' : '0.9rem',
          color: '#6b7280',
          fontWeight: '500'
        }}>
         Click on Confirm button once the payment has been received.
        </p>
      </div>

      {/* Loading State */}
      {isLoadingPurchases && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#6b7280'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(16, 185, 129, 0.2)',
            borderTop: '3px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem auto'
          }}></div>
          <div style={{ fontSize: '0.9rem' }}>Loading saved purchases...</div>
        </div>
      )}

      {/* Error Display */}
      {!isLoadingPurchases && purchasesError && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#dc2626',
          fontSize: '0.9rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            Error: {purchasesError}
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!isLoadingPurchases && !purchasesError && pendingPurchases.length === 0 && (
        <div style={{ 
          background: 'rgba(156, 163, 175, 0.1)',
          border: '1px solid rgba(156, 163, 175, 0.2)',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          color: '#6b7280'
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ margin: '0 auto 1rem auto', opacity: 0.5 }}>
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
          </svg>
          <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No Saved Purchases Found
          </div>
          <div style={{ fontSize: '0.9rem' }}>
            There are currently no pending purchases saved in the system.
          </div>
        </div>
      )}

      {/* Purchases List - Table Style */}
      {!isLoadingPurchases && !purchasesError && pendingPurchases.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Table Header */}
          {!isMobile && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.2fr',
              gap: '1rem',
              padding: '0.875rem 1rem',
              background: '#f9fafb',
              borderBottom: '2px solid #e5e7eb',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              <div>Distributor</div>
              <div>Date</div>
              <div>Items</div>
              <div>Status</div>
              <div>Amount</div>
              <div style={{ textAlign: 'center' }}>Action</div>
            </div>
          )}
          
          {/* Table Rows */}
          <div>
            {pendingPurchases.map((purchase: any, index: number) => (
              <div 
                key={purchase.id || purchase.pending_id || index}
                style={{
                  borderBottom: index < pendingPurchases.length - 1 ? '1px solid #f3f4f6' : 'none',
                  transition: 'background-color 0.2s ease',
                  animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {isMobile ? (
                  /* Mobile Card View */
                  <div style={{ padding: '1rem' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#1f2937',
                          marginBottom: '0.25rem'
                        }}>
                          {purchase.distributor_name || purchase.distributor?.name || purchase.distributor?.company_name || `Purchase #${purchase.pending_id || purchase.id || purchase.purchase_id || index + 1}`}
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          marginBottom: '0.5rem'
                        }}>
                          {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        background: isPendingConfirmation(purchase) 
                          ? 'rgba(245, 158, 11, 0.1)' 
                          : 'rgba(34, 197, 94, 0.1)',
                        color: isPendingConfirmation(purchase) 
                          ? '#d97706' 
                          : '#16a34a'
                      }}>
                        {getDisplayStatus(purchase)}
                      </div>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.75rem'
                    }}>
                      <div>
                        <span style={{ color: '#6b7280' }}>Items: </span>
                        <span style={{ fontWeight: '600', color: '#1f2937' }}>
                          {purchase.items?.length || 0}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Amount: </span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          ₹{purchase.total_amount || '0'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isDoctorAssociated ? (
                        <RequestButton
                          requestBody={buildPurchaseEditRequestBody(purchase)}
                          onRequestSuccess={() => handleRequestActionSuccess(purchase)}
                          onRequestError={handleRequestError}
                          label={purchase?.request_sent ? 'Request Sent' : 'Send Request'}
                          disabled={Boolean(purchase?.request_sent) || Boolean(purchase?.has_confirm_admin_request)}
                        />
                      ) : (
                        <ConfirmPurchaseButton
                          purchase={purchase}
                          onConfirmSuccess={handleConfirmSuccess}
                          onConfirmError={handleConfirmError}
                          label="Confirm"
                          variant="confirm"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  /* Desktop Table Row */
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.2fr',
                    gap: '1rem',
                    padding: '0.875rem 1rem',
                    alignItems: 'center',
                    fontSize: '0.875rem'
                  }}>
                    {/* Distributor Name */}
                    <div>
                      <div style={{
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '0.125rem'
                      }}>
                        {purchase.distributor_name || purchase.distributor?.name || purchase.distributor?.company_name || `Purchase #${purchase.pending_id || purchase.id || purchase.purchase_id || index + 1}`}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#9ca3af'
                      }}>
                        ID: {purchase.pending_id || purchase.id || purchase.purchase_id || 'N/A'}
                      </div>
                    </div>
                    
                    {/* Date */}
                    <div style={{ color: '#6b7280' }}>
                      {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      }) : 'N/A'}
                    </div>
                    
                    {/* Items Count */}
                    <div>
                      <div style={{
                        fontWeight: '600',
                        color: '#3b82f6'
                      }}>
                        {purchase.items?.length || 0} items
                      </div>
                    </div>
                    
                    {/* Status */}
                    <div>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: isPendingConfirmation(purchase) 
                          ? 'rgba(245, 158, 11, 0.1)' 
                          : 'rgba(34, 197, 94, 0.1)',
                        color: isPendingConfirmation(purchase) 
                          ? '#d97706' 
                          : '#16a34a'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: isPendingConfirmation(purchase) ? '#f59e0b' : '#10b981'
                        }}></div>
                        {getDisplayStatus(purchase)}
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div>
                      <div style={{
                        fontWeight: '700',
                        color: '#059669',
                        marginBottom: purchase.settled_amount !== undefined ? '0.125rem' : '0'
                      }}>
                        ₹{purchase.total_amount || '0'}
                      </div>
                      {purchase.settled_amount !== undefined && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#10b981'
                        }}>
                          Settled: ₹{purchase.settled_amount}
                        </div>
                      )}
                    </div>
                    
                    {/* Action */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {isDoctorAssociated ? (
                        <RequestButton
                          requestBody={buildPurchaseEditRequestBody(purchase)}
                          onRequestSuccess={() => handleRequestActionSuccess(purchase)}
                          onRequestError={handleRequestError}
                          label={purchase?.request_sent ? 'Request Sent' : 'Send Request'}
                          disabled={Boolean(purchase?.request_sent) || Boolean(purchase?.has_confirm_admin_request)}
                        />
                      ) : (
                        <ConfirmPurchaseButton
                          purchase={purchase}
                          onConfirmSuccess={handleConfirmSuccess}
                          onConfirmError={handleConfirmError}
                          label="Confirm"
                          variant="confirm"
                        />
                      )}
                    </div>
                </div>
                )}
                
                {/* Expandable Items Section */}
                {purchase.items && purchase.items.length > 0 && (
                  <div style={{
                    borderTop: '1px solid #f3f4f6',
                    background: '#fafbfc'
                  }}>
                    <div
                      onClick={() => togglePurchaseItems(purchase.id || purchase.pending_id || index)}
                      style={{
                        padding: isMobile ? '0.875rem 1rem' : '0.875rem 1.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background-color 0.2s ease',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{
                            transform: expandedPurchases.has(purchase.id || purchase.pending_id || index) ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                        Items ({purchase.items.length})
                      </div>
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#9ca3af',
                        fontWeight: '500'
                      }}>
                        {expandedPurchases.has(purchase.id || purchase.pending_id || index) ? 'Click to collapse' : 'Click to expand'}
                      </div>
                    </div>
                    
                    {expandedPurchases.has(purchase.id || purchase.pending_id || index) && (
                    <div style={{
                      padding: isMobile ? '0 1rem 1rem 1rem' : '0 1.5rem 1rem 1.5rem',
                      animation: 'fadeIn 0.2s ease-out'
                    }}>
                    <div style={{
                      display: 'grid',
                      gap: '0.5rem'
                    }}>
                      {purchase.items.map((item: any, itemIndex: number) => (
                        <div 
                          key={itemIndex}
                          style={{
                            padding: '0.875rem',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            fontSize: '0.8125rem'
                          }}
                        >
                          {isMobile ? (
                            /* Mobile Item View */
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                  {item.medicine_name || 'Unknown'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.125rem' }}>
                                  {item.medicine_company || 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                  {item.medicine_composition || 'N/A'}
                                </div>
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '0.5rem',
                                paddingTop: '0.5rem',
                                borderTop: '1px solid #f3f4f6',
                                fontSize: '0.75rem'
                              }}>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Batch: </span>
                                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{item.batch_no || 'N/A'}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>HSN: </span>
                                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{item.hsn_code || 'N/A'}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Qty: </span>
                                  {!isPurchaseReceived(purchase) && getPurchaseItemId(item) != null ? (
                                    editingQuantityItemId === getPurchaseItemId(item) ? (
                                      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        <input
                                          type="number"
                                          min={1}
                                          value={quantityEditValue[String(getPurchaseItemId(item))] ?? (item.quantity ?? '')}
                                          onChange={(e) => setQuantityEditValue(prev => ({ ...prev, [String(getPurchaseItemId(item))]: e.target.value }))}
                                          disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                          style={{
                                            width: '64px',
                                            padding: '0.25rem 0.5rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '0.8125rem',
                                            fontWeight: '600',
                                            color: '#3b82f6'
                                          }}
                                        />
                                        <span style={{ display: 'flex', gap: '0.25rem' }}>
                                          <button
                                            type="button"
                                            onClick={() => handleQuantitySave(item, purchase)}
                                            disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                            style={{
                                              padding: '0.2rem 0.4rem',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              background: '#10b981',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            {quantityUpdatingItemId === getPurchaseItemId(item) ? 'Saving...' : 'Save'}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => cancelEditQuantity(getPurchaseItemId(item)!)}
                                            disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                            style={{
                                              padding: '0.2rem 0.4rem',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              background: '#f3f4f6',
                                              color: '#374151',
                                              border: '1px solid #d1d5db',
                                              borderRadius: '6px',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Cancel
                                          </button>
                                        </span>
                                      </span>
                                    ) : (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: '600', color: '#3b82f6' }}>{item.quantity || 'N/A'}</span>
                                        <button
                                          type="button"
                                          onClick={() => startEditQuantity(item)}
                                          style={{
                                            padding: '0.2rem 0.4rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteItem(item, purchase)}
                                          disabled={deletingItemId === getPurchaseItemId(item)}
                                          style={{
                                            padding: '0.2rem 0.4rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: deletingItemId === getPurchaseItemId(item) ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          {deletingItemId === getPurchaseItemId(item) ? 'Removing...' : 'Delete'}
                                        </button>
                                      </span>
                                    )
                                  ) : (
                                    <span style={{ fontWeight: '600', color: '#3b82f6' }}>{item.quantity || 'N/A'}</span>
                                  )}
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Expiry: </span>
                                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{item.expiry_date || 'N/A'}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Unit Cost: </span>
                                  <span style={{ fontWeight: '600', color: '#10b981' }}>₹{item.unit_cost || '0'}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>MRP: </span>
                                  <span style={{ fontWeight: '600', color: '#10b981' }}>₹{item.mrp || '0'}</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Tax %: </span>
                                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{item.tax_percent || '0'}%</span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>Tax Amt: </span>
                                  <span style={{ fontWeight: '600', color: '#1f2937' }}>₹{item.tax_amount || '0'}</span>
                                </div>
                                <div style={{ gridColumn: '1 / -1', paddingTop: '0.25rem', borderTop: '1px solid #f3f4f6' }}>
                                  <span style={{ color: '#6b7280' }}>Line Total: </span>
                                  <span style={{ fontWeight: '700', color: '#059669', fontSize: '0.875rem' }}>₹{item.line_total || '0'}</span>
                                </div>
                                {item.storage_location && (
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <span style={{ color: '#6b7280' }}>Storage: </span>
                                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{item.storage_location}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            /* Desktop Item View */
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr',
                              gap: '0.75rem',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.125rem' }}>
                                  {item.medicine_name || 'Unknown'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                  {item.medicine_company || 'N/A'} • {item.medicine_composition || 'N/A'}
                                </div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>{item.batch_no || 'N/A'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Batch</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>{item.hsn_code || 'N/A'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>HSN</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                {!isPurchaseReceived(purchase) && getPurchaseItemId(item) != null ? (
                                  editingQuantityItemId === getPurchaseItemId(item) ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                                      <input
                                        type="number"
                                        min={1}
                                        value={quantityEditValue[String(getPurchaseItemId(item))] ?? (item.quantity ?? '')}
                                        onChange={(e) => setQuantityEditValue(prev => ({ ...prev, [String(getPurchaseItemId(item))]: e.target.value }))}
                                        disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                        style={{
                                          width: '72px',
                                          padding: '0.35rem 0.5rem',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '0.8125rem',
                                          fontWeight: '600',
                                          color: '#3b82f6',
                                          textAlign: 'right'
                                        }}
                                      />
                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button
                                          type="button"
                                          onClick={() => handleQuantitySave(item, purchase)}
                                          disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                          style={{
                                            padding: '0.25rem 0.5rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          {quantityUpdatingItemId === getPurchaseItemId(item) ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => cancelEditQuantity(getPurchaseItemId(item)!)}
                                          disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                          style={{
                                            padding: '0.25rem 0.5rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#f3f4f6',
                                            color: '#374151',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.35rem' }}>
                                        <span style={{ fontWeight: '600', color: '#3b82f6' }}>{item.quantity || 'N/A'}</span>
                                        <button
                                          type="button"
                                          onClick={() => startEditQuantity(item)}
                                          style={{
                                            padding: '0.2rem 0.45rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteItem(item, purchase)}
                                          disabled={deletingItemId === getPurchaseItemId(item)}
                                          style={{
                                            padding: '0.2rem 0.45rem',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: deletingItemId === getPurchaseItemId(item) ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          {deletingItemId === getPurchaseItemId(item) ? 'Removing...' : 'Delete'}
                                        </button>
                                      </div>
                                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Qty</div>
                                    </>
                                  )
                                ) : (
                                  <>
                                    <div style={{ fontWeight: '600', color: '#3b82f6' }}>{item.quantity || 'N/A'}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Qty</div>
                                  </>
                                )}
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                <div style={{ fontWeight: '600', color: '#10b981' }}>₹{item.unit_cost || '0'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Unit Cost</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                <div style={{ fontWeight: '600', color: '#10b981' }}>₹{item.mrp || '0'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>MRP</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>{item.tax_percent || '0'}%</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Tax %</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>₹{item.tax_amount || '0'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Tax Amt</div>
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'right' }}>
                                <div style={{ fontWeight: '700', color: '#059669' }}>₹{item.line_total || '0'}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Total</div>
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                <div>{item.expiry_date || 'N/A'}</div>
                                <div style={{ marginTop: '0.125rem' }}>Expiry</div>
                                {item.storage_location && (
                                  <div style={{ marginTop: '0.25rem', color: '#6b7280' }}>
                                    <div style={{ fontSize: '0.65rem' }}>📍 {item.storage_location}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Distributor Info */}
                    {(purchase.distributor_id || purchase.distributor) && (
                      <div style={{
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid #e5e7eb',
                        fontSize: '0.75rem',
                        color: '#6b7280'
                      }}>
                        <span style={{ fontWeight: '600' }}>Distributor: </span>
                        <span>{purchase.distributor?.name || purchase.distributor?.company_name || purchase.distributor_id || 'Not Assigned'}</span>
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                )}
                {hasAdminConfirmationStatus(purchase) && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: isAdminStatusPending(purchase) 
                      ? '#92400e' 
                      : '#14532d',
                    fontWeight: '400',
                    fontStyle: 'italic',
                    marginTop: '0.5rem',
                    marginBottom: '0.5rem',
                    paddingLeft: isMobile ? '1rem' : '1.5rem',
                    borderLeft: `2px solid ${isAdminStatusPending(purchase) 
                      ? '#f59e0b' 
                      : '#10b981'}`
                  }}>
                    {isAdminStatusPending(purchase) 
                      ? 'Admin review is pending for this purchase.' 
                      : 'Admin has approved this purchase.'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {receivePromptPurchase && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.45)',
          zIndex: 11000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '420px',
            padding: isMobile ? '1.25rem' : '1.75rem',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4"/>
                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                  </svg>
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#111827' }}>
                    Payment Confirmed
                  </h4>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    Click on the Received button if the stock has been delivered, or choose “Confirm later” .
                  </p>
                </div>
              </div>
              <div style={{
                background: '#f9fafb',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                fontSize: '0.85rem',
                color: '#4b5563'
              }}>
                Distributor:{' '}
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  {receivePromptPurchase.distributor_name || receivePromptPurchase.distributor?.name || `Purchase #${receivePromptPurchase.pending_id || receivePromptPurchase.id || ''}`}
                </span>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '0.75rem'
              }}>
                <button
                  type="button"
                  onClick={() => handleMarkAsReceived(receivePromptPurchase)}
                  disabled={receivingPurchaseId === activeReceiveId}
                  style={{
                    flex: 1,
                    background: receivingPurchaseId === activeReceiveId
                      ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: receivingPurchaseId === (receivePromptPurchase?.purchase_id || receivePromptPurchase?.id) ? 'not-allowed' : 'pointer',
                    padding: '0.5rem 1rem',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: receivingPurchaseId === activeReceiveId ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (receivingPurchaseId !== activeReceiveId) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (receivingPurchaseId !== activeReceiveId) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {receivingPurchaseId === activeReceiveId ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Receiving...
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                      </svg>
                      Received
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setReceivePromptPurchase(null)}
                  style={{
                    flex: 1,
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    fontWeight: 600,
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '0.5rem 1rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  Confirm later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default SavedPurchased;
